/**
 * Analytics Helper for KalkulatorDuit
 * 
 * Tracks page views and events in both preview and production environments.
 * Uses REACT_APP_BACKEND_URL for API calls.
 */

// Get API base URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

const API_BASE = getApiBaseUrl();

// Check if debug mode is enabled
const isDebugMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('debugAnalytics') === '1';
};

const isDevelopment = () => process.env.NODE_ENV === 'development';

/**
 * Determine page type from URL path
 */
function getPageType(pathname) {
  if (pathname.startsWith('/calculators/')) return 'calculator';
  if (pathname.startsWith('/guides/')) return 'guide';
  if (pathname.startsWith('/ai-tools/')) return 'ai-tool';
  if (pathname.startsWith('/news/')) return 'news';
  if (pathname === '/') return 'page';
  return 'page';
}

/**
 * Extract slug from URL path
 */
function getPageSlug(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  return parts[parts.length - 1] || 'home';
}

/**
 * Track page view - simplified endpoint for view counting
 * Uses the new /api/analytics/pageview endpoint
 */
export async function trackPageView(customPath = null) {
  const path = customPath || (typeof window !== 'undefined' ? window.location.pathname : '');
  
  // Skip admin pages
  if (path.startsWith('/admin')) return { status: 'skipped', reason: 'admin_page' };
  
  const pageType = getPageType(path);
  const pageSlug = getPageSlug(path);
  
  const payload = {
    page_url: path,
    page_type: pageType,
    page_slug: pageSlug
  };
  
  if (isDebugMode() || isDevelopment()) {
    console.log('[Analytics] Tracking pageview:', payload);
  }
  
  try {
    const response = await fetch(`${API_BASE}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });
    
    const result = await response.json();
    
    if (isDebugMode() || isDevelopment()) {
      console.log('[Analytics] Pageview response:', result);
    }
    
    return result;
  } catch (error) {
    if (isDevelopment() || isDebugMode()) {
      console.error('[Analytics] Pageview tracking failed:', error);
    }
    return { status: 'error', message: error.message };
  }
}

/**
 * Track an analytics event (generic tracking)
 */
export async function trackEvent({
  event,
  module = null,
  source = null,
  path = null,
  timestamp = null,
  metadata = null
}) {
  const payload = {
    event,
    module,
    source: source || getTrafficSource(),
    path: path || (typeof window !== 'undefined' ? window.location.pathname : null),
    timestamp: timestamp || new Date().toISOString(),
    metadata
  };
  
  if (isDebugMode() || isDevelopment()) {
    console.log('[Analytics] Tracking event:', payload);
  }
  
  try {
    const response = await fetch(`${API_BASE}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });
    
    return await response.json();
  } catch (error) {
    if (isDevelopment() || isDebugMode()) {
      console.error('[Analytics] Event tracking failed:', error);
    }
    return { status: 'error', message: error.message };
  }
}

/**
 * Detect traffic source from referrer or UTM params
 */
function getTrafficSource() {
  if (typeof window === 'undefined') return 'unknown';
  
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  if (utmSource) return utmSource;
  
  const referrer = document.referrer;
  if (!referrer) return 'direct';
  
  try {
    const hostname = new URL(referrer).hostname;
    if (hostname.includes('google')) return 'google';
    if (hostname.includes('bing')) return 'bing';
    if (hostname.includes('facebook') || hostname.includes('fb.com')) return 'facebook';
    if (hostname.includes('twitter') || hostname.includes('t.co')) return 'twitter';
    if (hostname.includes('instagram')) return 'instagram';
    if (hostname === window.location.hostname) return 'internal';
    return 'referral';
  } catch {
    return 'unknown';
  }
}

/**
 * Track calculator usage
 */
export function trackCalculatorUsage(calculatorType, inputData = {}, resultData = {}) {
  return trackEvent({
    event: 'calculator_used',
    module: calculatorType,
    metadata: { input_summary: inputData, result_summary: resultData }
  });
}

/**
 * Track lead capture
 */
export function trackLeadCapture(source, ctaType) {
  return trackEvent({
    event: 'lead_captured',
    module: 'lead_form',
    source,
    metadata: { cta_type: ctaType }
  });
}

/**
 * Track button click
 */
export function trackClick(elementName, additionalData = {}) {
  return trackEvent({
    event: 'click',
    module: elementName,
    metadata: additionalData
  });
}

export const ANALYTICS_CONFIG = {
  API_BASE,
  isDebugMode,
  isDevelopment
};

export default {
  trackEvent,
  trackPageView,
  trackCalculatorUsage,
  trackLeadCapture,
  trackClick,
  ANALYTICS_CONFIG
};
